import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, Package, Dna } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import {
  PageHeader, Spinner, EmptyState, Modal, Field, GoldButton, GhostButton, inputCls,
} from "@/components/app/widgets";

const EMPTY = {
  name: "", description: "", category: "", niche: "", target_customer: "",
  problem_solved: "", benefits: "", pricing: "", landing_page: "", product_url: "",
  lead_magnet: "", cta: "", geo_markets: "", languages: "", keywords: "",
  competitors: "", partnership_types: "",
};

const FIELDS = [
  ["name", "Product name", true],
  ["description", "Description", false, "textarea"],
  ["category", "Category"],
  ["niche", "Niche"],
  ["target_customer", "Target customer", false, "textarea"],
  ["problem_solved", "Main problem solved", false, "textarea"],
  ["benefits", "Benefits", false, "textarea"],
  ["pricing", "Pricing (e.g. $79 / $147 / $297)"],
  ["landing_page", "Landing page URL"],
  ["product_url", "Product URL"],
  ["lead_magnet", "Lead magnet"],
  ["cta", "Call to action"],
  ["geo_markets", "Geographic markets"],
  ["languages", "Languages (comma separated)"],
  ["keywords", "Keywords (comma separated)"],
  ["competitors", "Competitors / alternatives"],
  ["partnership_types", "Preferred partnership types (comma separated)"],
];

function toList(v) {
  if (Array.isArray(v)) return v;
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}

export default function Products() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...EMPTY, ...p,
      languages: (p.languages || []).join(", "),
      keywords: (p.keywords || []).join(", "),
      partnership_types: (p.partnership_types || []).join(", "),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      languages: toList(form.languages),
      keywords: toList(form.keywords),
      partnership_types: toList(form.partnership_types),
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" and its campaigns?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!items) return <Spinner label="Loading products" />;

  return (
    <div data-testid="products-page">
      <PageHeader title="Product Catalog" sub="Every product your distribution engine serves.">
        <GoldButton onClick={openCreate} data-testid="new-product-button"><Plus className="h-4 w-4" /> New Product</GoldButton>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products yet"
          desc="Create your first product profile — the engine is product-agnostic."
          action={<GoldButton onClick={openCreate}><Plus className="h-4 w-4" /> Create Product</GoldButton>}
        />
      ) : (
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {items.map((p) => (
            <div key={p.product_id} data-testid={`product-card-${p.product_id}`} className="group border border-[#27272A] bg-[#0A0A0A] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#D4AF37]/50">
              <div className="flex items-start justify-between gap-3">
                <Link to={`/app/products/${p.product_id}`} data-testid={`open-product-${p.product_id}`} className="font-serif text-xl font-semibold hover:text-[#D4AF37]">
                  {p.name}
                </Link>
                <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.product_id}`} className="p-1.5 text-zinc-500 hover:text-white"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} data-testid={`delete-product-${p.product_id}`} className="p-1.5 text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {p.dna_completeness && (
                <Link to={`/app/products/${p.product_id}`} className="mt-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider">
                  <Dna className="h-3.5 w-3.5 text-[#D4AF37]" />
                  <span className={p.dna_completeness.complete ? "text-emerald-400" : "text-amber-400"}>
                    Product DNA {p.dna_completeness.percent}%{p.dna_completeness.complete ? " — complete" : " — incomplete"}
                  </span>
                </Link>
              )}
              <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#D4AF37]">{p.niche || p.category || "General"}</p>
              <p className="mt-3 line-clamp-2 text-sm text-zinc-400">{p.description}</p>
              <div className="mt-4 space-y-1.5 text-xs text-zinc-500">
                {p.target_customer && <p><span className="text-zinc-300">Audience:</span> {p.target_customer}</p>}
                {p.pricing && <p><span className="text-zinc-300">Pricing:</span> {p.pricing}</p>}
                {p.lead_magnet && <p><span className="text-zinc-300">Lead magnet:</span> {p.lead_magnet}</p>}
              </div>
              {p.keywords?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.keywords.slice(0, 5).map((k) => (
                    <span key={k} className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] text-zinc-400">{k}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Product" : "New Product"} wide>
        <form onSubmit={save} data-testid="product-form" className="grid gap-4 sm:grid-cols-2">
          {FIELDS.map(([key, label, required, kind]) => (
            <div key={key} className={kind === "textarea" ? "sm:col-span-2" : ""}>
              <Field label={label}>
                {kind === "textarea" ? (
                  <textarea
                    rows={2} required={!!required} data-testid={`product-field-${key}`}
                    value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className={inputCls}
                  />
                ) : (
                  <input
                    type="text" required={!!required} data-testid={`product-field-${key}`}
                    value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className={inputCls}
                  />
                )}
              </Field>
            </div>
          ))}
          <div className="flex justify-end gap-3 sm:col-span-2">
            <GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton>
            <GoldButton type="submit" disabled={saving} data-testid="product-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Create Product"}
            </GoldButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
